import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IAcquisition } from '@models/acquisition';
import { ICalibrationStatusUpdateRequest } from '@models/calibrations/calibration-status-update-request';
import { ILocalStorage } from '@models/localStorage';
import { IRecalculationRequest } from '@models/recalculation/recalculation-request';
import { RecalculationTrigger } from '@models/recalculation/recalculation-trigger';
import { CalibrationStatus } from '@models/record-status';
import { ForecastMethods, IForecastSettings } from '@models/stable-balance/forecast-settings';
import { IHistoricalSettings } from '@models/stable-balance/historical-settings';
import {
  emptyStableBalanceCalibration,
  IStableBalanceCalibration,
  IStableBalanceSeries
} from '@models/stable-balance/stable-balance-calibration';
import {
  IDateRange,
  IStableBalanceEstimateQuery,
  IStableBalanceEstimateResult,
  IUnadjustedBalances
} from '@models/stable-balance/unadjusted-balance';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { LocalStorageService } from '@services/local-storage.service';
import { MenuService } from '@services/menu.service';
import { ObservableStore } from '@services/observable-store/dist';
import { RecalculationService } from '@services/recalculation/recalculation.service';
import { clone } from '@shared/arrays';
import { getDateString, getLastDayOfMonth, getLastDayOfNextMonth, isValidDate, monthDiff } from '@shared/dates';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const estimateNames = ['Poly-1st', 'Poly-2nd', 'Poly-3rd', 'Poly-4th', 'Poly-5th', 'Poly-6th'];

@Injectable({
  providedIn: 'root'
})
export class StableBalanceService extends ObservableStore<IStoreState> {
  /*****************
   * API ENDPOINTS *
   *****************/
  private STABLE_BALANCE_API: string;
  private STABLE_BALANCE_CALIBRATION_API: string;
  // Append With the product id
  private STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS: string;
  private ESTIMATE_API: string;

  private readonly MAX_FUNCTIONAL_FORMS = 6;
  private _currentCalibration: IStableBalanceCalibration = clone(emptyStableBalanceCalibration);

  get currentCalibration() {
    return this._currentCalibration;
  }
  set currentCalibration(calibration: IStableBalanceCalibration) {
    this._currentCalibration = calibration;

    if (this._currentCalibration && this._currentCalibration.ChangeLogs) {
      delete this._currentCalibration.ChangeLogs;
    }

    this.historicalSettings = calibration && calibration.Settings ? calibration.Settings.Historical : null;
    this.forecastSettings = calibration && calibration.Settings ? calibration.Settings.Forecast : null;
    this.selectedPolynomialForm = calibration ? calibration.SelectedPolynomialForm : null;

    if (calibration) {
      this._currentCalibration.CalibrationId = null;
      this._currentCalibration.RecordStatus = CalibrationStatus.Proposed;
    }
  }

  // /**
  //  * Adjusted Balance
  //  */
  // private readonly adjustments$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);

  // get adjustments(): IStableBalanceSeries[] {
  //   return this.adjustments$.value;
  // }

  // set adjustments(adjustments: IStableBalanceSeries[]) {
  //   this.currentCalibration.Adjustments = this.filterAdjustments(adjustments, new Date(this.calibrationStartDate));
  //   this.adjustments$.next(adjustments);
  // }

  // get adjustmentsListener$(): Observable<IStableBalanceSeries[]> {
  //   return this.adjustments$.asObservable();
  // }

  // Total Balances
  private readonly totalBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get totalBalances(): IStableBalanceSeries[] {
    return [...this.totalBalances$.value, ...this.forecastedTotalBalances$.value];
  }
  set totalBalances(totalBalances: IStableBalanceSeries[]) {
    this.totalBalances$.next(totalBalances);
  }
  get totalBalanceListener$(): Observable<IStableBalanceSeries[]> {
    return this.totalBalances$.asObservable();
  }

  // Forecasted Total Balances
  private readonly forecastedTotalBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedTotalBalances(): IStableBalanceSeries[] {
    return this.forecastedTotalBalances$.value;
  }
  set forecastedTotalBalances(forecastedTotalBalances: IStableBalanceSeries[]) {
    this.forecastedTotalBalances$.next(forecastedTotalBalances);
  }

  // Fitted Balances
  private readonly fittedBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get fittedBalances(): IStableBalanceSeries[] {
    return this.fittedBalances$.value;
  }
  set fittedBalances(fittedBalances: IStableBalanceSeries[]) {
    this.fittedBalances$.next(fittedBalances);
  }

  // Volatility Adjustments
  private readonly volatilityAdjustments$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get volatilityAdjustments(): IStableBalanceSeries[] {
    return this.volatilityAdjustments$.value;
  }
  set volatilityAdjustments(volatilityAdjustments: IStableBalanceSeries[]) {
    this.volatilityAdjustments$.next(volatilityAdjustments);
  }

  // Growth Rates
  private readonly growthRates$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get growthRates(): IStableBalanceSeries[] {
    return [...this.growthRates$.value, ...this.forecastedGrowthRates$.value];
  }
  set growthRates(growthRates: IStableBalanceSeries[]) {
    this.growthRates$.next(growthRates);
  }
  private readonly forecastedGrowthRates$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedGrowthRates(): IStableBalanceSeries[] {
    return this.forecastedGrowthRates$.value;
  }
  set forecastedGrowthRates(forecastedGrowthRates: IStableBalanceSeries[]) {
    this.forecastedGrowthRates$.next(forecastedGrowthRates);
  }

  // Constrained Rates
  private readonly constrainedRates$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get constrainedRates(): IStableBalanceSeries[] {
    return [...this.constrainedRates$.value, ...this.forecastedConstrainedRates$.value];
  }
  set constrainedRates(constrainedRates: IStableBalanceSeries[]) {
    this.constrainedRates$.next(constrainedRates);
  }

  private readonly forecastedConstrainedRates$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedConstrainedRates(): IStableBalanceSeries[] {
    return this.forecastedConstrainedRates$.value;
  }
  set forecastedConstrainedRates(forecastedConstrainedRates: IStableBalanceSeries[]) {
    this.forecastedConstrainedRates$.next(forecastedConstrainedRates);
  }

  // Non-Stable Balances
  private readonly nonStableBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get nonStableBalances(): IStableBalanceSeries[] {
    return [...this.nonStableBalances$.value, ...this.forecastedNonStableBalances$.value];
  }
  set nonStableBalances(nonStableBalances: IStableBalanceSeries[]) {
    this.nonStableBalances$.next(nonStableBalances);
  }
  private readonly forecastedNonStableBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedNonStableBalances(): IStableBalanceSeries[] {
    return this.forecastedNonStableBalances$.value;
  }
  set forecastedNonStableBalances(forecastedNonStableBalances: IStableBalanceSeries[]) {
    this.forecastedNonStableBalances$.next(forecastedNonStableBalances);
  }

  // Non-Stable Percents
  private readonly nonStablePercents$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get nonStablePercents(): IStableBalanceSeries[] {
    return [...this.nonStablePercents$.value, ...this.forecastedNonStablePercents$.value];
  }
  set nonStablePercents(nonStablePercents: IStableBalanceSeries[]) {
    this.nonStablePercents$.next(nonStablePercents);
  }
  private readonly forecastedNonStablePercents$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedNonStablePercents(): IStableBalanceSeries[] {
    return this.forecastedNonStablePercents$.value;
  }
  set forecastedNonStablePercents(forecastedNonStablePercents: IStableBalanceSeries[]) {
    this.forecastedNonStablePercents$.next(forecastedNonStablePercents);
  }

  private readonly forecastedStableBalances$: BehaviorSubject<IStableBalanceSeries[]> = new BehaviorSubject<IStableBalanceSeries[]>([]);
  get forecastedStableBalances(): IStableBalanceSeries[] {
    return this.forecastedStableBalances$.value;
  }
  set forecastedStableBalances(forecastedStableBalances: IStableBalanceSeries[]) {
    this.forecastedStableBalances$.next(forecastedStableBalances);
  }

  /**
   *  Start and End Date
   */

  private readonly dateRange$: BehaviorSubject<IDateRange> = new BehaviorSubject<IDateRange>(null);

  get dateRange() {
    return this.dateRange$.value;
  }

  set dateRange(dateRange: IDateRange) {
    const { StartDate, EndDate } = dateRange;
    if (isValidDate(StartDate) && isValidDate(EndDate)) {
      dateRange.StartDate = getLastDayOfMonth(dateRange.StartDate);
      dateRange.EndDate = getLastDayOfMonth(dateRange.EndDate);

      if (this.currentCalibration) {
        this.currentCalibration.CalibrationStartDate = getDateString(StartDate);
      }
      this.dateRange$.next(dateRange);
    }
  }

  set calibrationStartDate(calibrationStartDate: Date) {
    if (isValidDate(calibrationStartDate) && getDateString(calibrationStartDate) !== getDateString(this.calibrationStartDate)) {
      const dateRange: IDateRange = {
        EndDate: null,
        ...this.dateRange,
        StartDate: getLastDayOfMonth(calibrationStartDate)
      };

      if (this.currentCalibration) {
        this.currentCalibration.CalibrationStartDate = getDateString(calibrationStartDate) + 'T00:00:00';
      }
      this.dateRange$.next(dateRange);
    }
  }

  get calibrationStartDate(): Date {
    return this.dateRange ? this.dateRange.StartDate : null;
  }

  /**
   * Last day of previous month
   */
  get defaultCalibrationEndDate(): Date {
    return getLastDayOfNextMonth(new Date(), -1);
  }

  get dateRangeListener$(): Observable<IDateRange> {
    return this.dateRange$.asObservable();
  }

  /*****************
   *  Acquisitions *
   *****************/

  private readonly selectedAcquisitions$: BehaviorSubject<IAcquisition[]> = new BehaviorSubject<IAcquisition[]>([]);

  get selectedAcquisitions() {
    return this.selectedAcquisitions$.value;
  }

  set selectedAcquisitions(selectedAcquisitions: IAcquisition[]) {
    const newAcquisitions = Array.from(new Set(selectedAcquisitions.map(acq => acq.AcquisitionId))).sort();

    if (this.currentCalibration && JSON.stringify(newAcquisitions) !== JSON.stringify(this.currentCalibration.SelectedAcquisitionIds)) {
      this.currentCalibration.SelectedAcquisitionIds = newAcquisitions;
      this.selectedAcquisitions$.next(selectedAcquisitions);
    }
  }

  // get selectedAcquisitionListener$(): Observable<IAcquisition[]> {
  //   return this.selectedAcquisitions$.asObservable();
  // }

  /*********************************
   *                               *
   *  ESTIMATES / Polynomial Form  *
   *             START             *
   *********************************/
  public readonly estimates$: BehaviorSubject<IStableBalanceEstimateResult[]> = new BehaviorSubject<IStableBalanceEstimateResult[]>([]);
  public readonly selectedPolynomialForm$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  set estimates(estimates: IStableBalanceEstimateResult[]) {
    const coefficients = [];
    estimates.forEach(est => coefficients.push(est.CoEfficients));

    // It's possible that selected estimate doesn't longer exist, checking and updating if necessary
    if (estimates.length > 0 && this.selectedPolynomialForm >= estimates.length) {
      this.selectedPolynomialForm = this.estimates.length - 1;
    }

    const estimate = this.estimates[this.selectedPolynomialForm];

    if (estimate && this.currentCalibration) {
      this.currentCalibration.Coefficients = coefficients;
      this.currentCalibration.SelectedAcquisitionIds = estimate.SelectedAcquisitionIds;
      this.currentCalibration.FittedBalances = estimate.FittedBalanceSeries;
    }

    this.estimates$.next(estimates);
  }

  get estimates(): IStableBalanceEstimateResult[] {
    return this.estimates$.getValue();
  }

  set selectedPolynomialForm(polynomialForm: number) {
    // tslint:disable-next-line:triple-equals
    if (polynomialForm == null) {
      return;
    }

    if (this.estimates.length > 0) {
      if (polynomialForm === this.selectedPolynomialForm || polynomialForm > this.estimates.length - 1) {
        return;
      }
    }

    if (this.currentCalibration && polynomialForm >= 0 && polynomialForm < this.MAX_FUNCTIONAL_FORMS) {
      const estimate = this.estimates[polynomialForm];

      if (estimate) {
        this.currentCalibration.SelectedPolynomialForm = polynomialForm;
        const coefficients = [];
        this.estimates.forEach(est => coefficients.push(est.CoEfficients));

        this.currentCalibration.Coefficients = coefficients;
        this.currentCalibration.SelectedAcquisitionIds = estimate.SelectedAcquisitionIds;
        this.currentCalibration.FittedBalances = estimate.FittedBalanceSeries;
      }

      if (polynomialForm !== this.selectedPolynomialForm) {
        this.selectedPolynomialForm$.next(polynomialForm);
      }
    }
  }

  get selectedPolynomialForm() {
    return this.selectedPolynomialForm$.getValue();
  }

  /*********************************
   *                               *
   *  ESTIMATES / Polynomial Form  *
   *             END               *
   *********************************/

  /************************************
   * Historical Settings Listeners *
   ************************************/
  private readonly historicalSettings$: BehaviorSubject<IHistoricalSettings> = new BehaviorSubject<IHistoricalSettings>(null);

  get historicalSettings() {
    return this.historicalSettings$.value;
  }

  set historicalSettings(historicalSettings: IHistoricalSettings) {
    this.fixHistoricalSettings(historicalSettings);
    this.historicalSettings$.next(historicalSettings);
  }

  get historicalSettingsListener$() {
    return this.historicalSettings$.asObservable();
  }

  /*********************
   * Forecast Settings *
   *********************/

  private readonly forecastSettings$: BehaviorSubject<IForecastSettings> = new BehaviorSubject<IForecastSettings>(null);

  get forecastSettings() {
    return this.forecastSettings$.value;
  }

  set forecastSettings(forecastSettings: IForecastSettings) {
    this.fixForecastSettings(forecastSettings);
    this.forecastSettings$.next(forecastSettings);
  }

  get forecastSettingsListener$() {
    return this.forecastSettings$.asObservable();
  }

  public canWriteToLocalStorage = false;

  public canDeactivate = true;

  constructor(
    private readonly appConfig: AppConfigProvider,

    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly menu: MenuService,
    private readonly recalculationService: RecalculationService
  ) {
    super({ trackStateHistory: false, logStateChanges: false });

    this.appConfig.environment.subscribe(env => {
      this.STABLE_BALANCE_API = `${env.API.STABLE_BALANCE}/api/stablebalance/balances`;
      this.STABLE_BALANCE_CALIBRATION_API = `${env.API.STABLE_BALANCE}/api/stablebalance/calibration`;
      // Append With the product id
      this.STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS = `${env.API.STABLE_BALANCE}/api/stablebalance/calibrationforproduct`;
      this.ESTIMATE_API = `${env.API.STABLE_BALANCE}/api/stablebalance/estimate`;
    });
  }

  public getUnadjustedBalances(productId: number): Observable<IUnadjustedBalances> {
    const query = {
      productId
    };
    return this.http.post<IUnadjustedBalances>(this.STABLE_BALANCE_API, query).pipe(
      // Just re-ordering properties
      map(unadjustedBalances => {
        if (unadjustedBalances && unadjustedBalances.BalanceSeries) {
          unadjustedBalances.BalanceSeries = unadjustedBalances.BalanceSeries.map(ub => ({
            PeriodEndDate: getDateString(ub.PeriodEndDate),
            Balance: ub.Balance,
            IsHistorical: true
          }));
        }
        this.setState({ unadjustedBalances }, StoreActions.GetUnadjustedBalances);
        return unadjustedBalances;
      })
    );
  }

  /**
   * Updates returns **TotalBalances** array calculated from calibration's **UnadjustedBalances** and **Adjustments**.
   */
  public getTotalBalances(calibration: IStableBalanceCalibration): IStableBalanceSeries[] {
    let totalBalances: IStableBalanceSeries[] = [];
    if (calibration) {
      calibration.UnadjustedBalances.forEach((unadjustedBalance, i) => {
        const adjustment = calibration.Adjustments.find(
          ({ PeriodEndDate }) => getDateString(PeriodEndDate) === getDateString(unadjustedBalance.PeriodEndDate)
        );

        totalBalances.push({
          PeriodEndDate: getDateString(unadjustedBalance.PeriodEndDate),
          Balance: unadjustedBalance.Balance + (adjustment ? adjustment.Balance : 0),
          IsHistorical: true
        });
      });

      const forecasts = calibration.TotalBalances.filter(
        b => !b.IsHistorical && new Date(b.PeriodEndDate) > new Date(calibration.CalibrationEndDate)
      );

      totalBalances = [...totalBalances, ...forecasts]
        .sort((a, b) => new Date(a.PeriodEndDate).getTime() - new Date(b.PeriodEndDate).getTime())
        .sort((a, b) => (a.IsHistorical === b.IsHistorical ? 0 : a.IsHistorical ? -1 : 1));
    }
    return totalBalances;
  }

  public async getSavedData(
    productId: number
  ): Promise<{
    activeCalibration: IStableBalanceCalibration;
    calibrations: IStableBalanceCalibration[];
    lsCalibration: IStableBalanceCalibration;
    proposedCalibration: IStableBalanceCalibration;
    rejectedCalibration: IStableBalanceCalibration;
    selectedCalibration: IStableBalanceCalibration;
  }> {
    let activeCalibration: IStableBalanceCalibration = null;
    let calibrations: IStableBalanceCalibration[];
    let lsCalibration: ILocalStorage<IStableBalanceCalibration>;
    let proposedCalibration: IStableBalanceCalibration = null;
    let rejectedCalibration: IStableBalanceCalibration = null;
    let selectedCalibration: IStableBalanceCalibration = null;

    const results = await forkJoin([
      this.getCalibrationsForProduct({ productId }),
      of(this.localStorageService.fetchFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, productId))
    ])
      .toPromise()
      .then()
      .catch(error => {
        console.error(error);
      });

    if (results) {
      calibrations = results[0];
      rejectedCalibration = calibrations.find(c => c.RecordStatus === CalibrationStatus.Rejected);
      proposedCalibration = calibrations.find(c => c.RecordStatus === CalibrationStatus.Proposed);
      activeCalibration = calibrations.find(c => c.RecordStatus === CalibrationStatus.Active);

      const dbCalibration = rejectedCalibration || proposedCalibration || activeCalibration;

      lsCalibration = results[1] as ILocalStorage<IStableBalanceCalibration>;

      if (lsCalibration) {
        if (dbCalibration) {
          // Perform date comparison
          if (
            new Date(dbCalibration.DateTimeCreated.substr(0, 19) || dbCalibration.DateTimeModified.substr(0, 19)) >=
            new Date(lsCalibration.storeDate.substr(0, 19))
          ) {
            // If local storage calibration is older than DB one, we discard it
            this.discardLocalStorage(productId);
            // Choose DB Calibration
            selectedCalibration = clone(dbCalibration);
          } else {
            // Choose Local Storage Calibration
            this.canDeactivate = false;
            selectedCalibration = clone(lsCalibration.payload);
            selectedCalibration.RecordStatus = dbCalibration.RecordStatus || CalibrationStatus.Proposed;
            selectedCalibration.CalibrationId = dbCalibration.CalibrationId;
          }
        } else {
          // Return local storage
          this.canDeactivate = false;
          selectedCalibration = clone(lsCalibration.payload);
          selectedCalibration.RecordStatus = CalibrationStatus.Proposed;
        }
      } else if (dbCalibration) {
        // Return db storage
        this.canDeactivate = true;
        selectedCalibration = dbCalibration;
      } else {
        this.canDeactivate = true;
      }
    }

    this.loadCalibration(selectedCalibration, false);

    return of({
      selectedCalibration,
      rejectedCalibration,
      proposedCalibration,
      activeCalibration,
      calibrations,
      lsCalibration: lsCalibration ? lsCalibration.payload : null
    }).toPromise();
  }

  public discardLocalStorage(productId: number): void {
    this.clearValues();
    this.localStorageService.clearFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, productId);
    this.canDeactivate = true;
  }

  public updateLocalStorage(cal: IStableBalanceCalibration) {
    if (cal && cal.ProductId !== null) {
      const calibration: IStableBalanceCalibration = clone(cal);
      calibration.RecordStatus = CalibrationStatus.Proposed;
      calibration.CalibrationId = null;
      this.localStorageService.storeInLocalStorage<IStableBalanceCalibration>(
        calibration,
        LocalStorageKeys.STABLE_BALANCE,
        this.menu.selectedProduct.ProductId
      );

      this.canDeactivate = false;
    }
  }

  /**
   * Saves An Array Of Calibration
   */
  public saveCalibrationToDB(calibrations: IStableBalanceCalibration[]): Observable<IStableBalanceCalibration[]> {
    if (calibrations) {
      const clonedCalibrations: IStableBalanceCalibration[] = clone(calibrations);
      clonedCalibrations.forEach(calibration => {
        this.fixSettings(calibration);
        calibration.Adjustments = this.filterAdjustments(calibration.Adjustments, new Date(calibration.CalibrationStartDate));
        calibration.FormId = 'Save Form';
        delete calibration.ChangeLogs;
        delete calibration.UnadjustedBalances;
      });

      return this.http.post<IStableBalanceCalibration[]>(this.STABLE_BALANCE_CALIBRATION_API, clonedCalibrations).pipe(
        tap((savedCalibrations: IStableBalanceCalibration[]) => {
          if (savedCalibrations.length > 0) {
            const productIds = Array.from(new Set(savedCalibrations.map(c => c.ProductId)));
            const recalcRequest: IRecalculationRequest = {
              ProductIds: productIds,
              RecalculationTrigger: RecalculationTrigger.CalibrationSave
            };
            this.recalculationService.startCalculation(recalcRequest).toPromise();
          }
        }),

        map((savedCalibrations: IStableBalanceCalibration[]) => {
          savedCalibrations.forEach(cal => {
            this.localStorageService.clearFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, cal.ProductId);
            this.canDeactivate = true;
            this.fixSettings(cal);
          });
          return savedCalibrations;
        })
      );
    }
  }

  public saveAndApproveCalibration(calibration: IStableBalanceCalibration) {
    const clonedCalibration: IStableBalanceCalibration = { ...calibration, FormId: 'Save and Approval Form' };
    this.fixSettings(clonedCalibration);
    clonedCalibration.Adjustments = this.filterAdjustments(clonedCalibration.Adjustments, new Date(clonedCalibration.CalibrationStartDate));
    delete clonedCalibration.ChangeLogs;
    delete clonedCalibration.UnadjustedBalances;

    return this.http.post<IStableBalanceCalibration>(`${this.STABLE_BALANCE_CALIBRATION_API}/save-approve`, clonedCalibration).pipe(
      tap(cal => {
        const recalcRequest: IRecalculationRequest = {
          ProductIds: [cal.ProductId],
          RecalculationTrigger: RecalculationTrigger.CalibrationSaveApprove
        };
        this.recalculationService.startCalculation(recalcRequest).toPromise();
      }),
      map(cal => {
        this.localStorageService.clearFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, cal.ProductId);
        this.canDeactivate = true;
        this.fixSettings(cal);
        return cal;
      })
    );
  }

  public approveCalibration(calibration: IStableBalanceCalibration) {
    const approvalRequest: ICalibrationStatusUpdateRequest = {
      CalibrationId: calibration.CalibrationId,
      Comment: calibration.Comment,
      FormId: 'Approval Form',
      DateTimeCreated: calibration.DateTimeCreated,
      DateTimeModified: calibration.DateTimeModified
    };
    return this.http.post<IStableBalanceCalibration>(`${this.STABLE_BALANCE_CALIBRATION_API}/approve`, approvalRequest).pipe(
      tap(cal => {
        const recalcRequest: IRecalculationRequest = {
          ProductIds: [cal.ProductId],
          RecalculationTrigger: RecalculationTrigger.CalibrationApprove
        };
        this.recalculationService.startCalculation(recalcRequest).toPromise();
      }),
      map(cal => {
        this.localStorageService.clearFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, cal.ProductId);
        this.canDeactivate = true;
        this.fixSettings(cal);
        return cal;
      })
    );
  }

  public rejectCalibration(calibration: IStableBalanceCalibration) {
    const request: ICalibrationStatusUpdateRequest = {
      CalibrationId: calibration.CalibrationId,
      Comment: calibration.Comment,
      FormId: 'Rejection Form',
      DateTimeCreated: calibration.DateTimeCreated,
      DateTimeModified: calibration.DateTimeModified
    };
    return this.http.post<IStableBalanceCalibration>(`${this.STABLE_BALANCE_CALIBRATION_API}/reject`, request).pipe(
      tap(cal => {
        const recalcRequest: IRecalculationRequest = {
          ProductIds: [cal.ProductId],
          RecalculationTrigger: RecalculationTrigger.CalibrationApprove
        };
        this.recalculationService.startCalculation(recalcRequest, 'Calibration Rejected').toPromise();
      }),
      map(cal => {
        this.localStorageService.clearFromLocalStorage(LocalStorageKeys.STABLE_BALANCE, cal.ProductId);
        this.canDeactivate = true;
        this.fixSettings(cal);
        return cal;
      })
    );
  }

  public getCalibrationsForProduct({
    productId = null,
    excludeInactive = false,
    excludeTimeSeries = false
  }: {
    productId?: number;
    excludeInactive?: boolean;
    excludeTimeSeries?: boolean;
  }): Observable<IStableBalanceCalibration[]> {
    const url = productId === null ? this.STABLE_BALANCE_CALIBRATION_API : `${this.STABLE_BALANCE_CALIBRATIONS_FOR_PRODUCTS}/${productId}`;

    let params: HttpParams = new HttpParams();
    if (excludeInactive) {
      params = params.append('excludeInactive', 'true');
    }
    if (excludeTimeSeries) {
      params = params.append('excludeTimeSeries', 'true');
    }

    return this.http
      .get<IStableBalanceCalibration[]>(url, { params })
      .pipe(
        map(calibrations => {
          calibrations
            .sort((a, b) => new Date(b.DateTimeCreated).getTime() - new Date(a.DateTimeCreated).getTime())
            .forEach(calibration => {
              this.fixSettings(calibration);

              // @ts-ignore
              const selectedAcquisitionIdsStr: string = calibration.SelectedAcquisitionIds || '';
              calibration.SelectedAcquisitionIds = Array.from(
                // <6641>
                new Set(
                  selectedAcquisitionIdsStr
                    .split(',')
                    .filter(x => x)
                    .map(x => parseInt(x, 10))
                    .sort()
                )
              );

              if (calibration.StableBalances) {
                calibration.StableBalances.sort((a, b) => new Date(a.PeriodEndDate).getTime() - new Date(b.PeriodEndDate).getTime());
              }
              if (calibration.Adjustments) {
                calibration.Adjustments.sort((a, b) => new Date(a.PeriodEndDate).getTime() - new Date(b.PeriodEndDate).getTime());
              }
            });
          return calibrations;
        })
      );
  }

  public getActiveCalibrationForProduct(productId: number): Observable<IStableBalanceCalibration> {
    return this.getCalibrationsForProduct({ productId, excludeInactive: true }).pipe(
      map((calibrations: IStableBalanceCalibration[]) => {
        const calibration = calibrations.find(cal => cal.RecordStatus === CalibrationStatus.Active);
        return calibration;
      })
    );
  }
  /**
   *
   * @param calibration The calibration that you want loaded into the stable balance module
   * @param clear if you want to clear all values stored in the service set to true by default
   */
  public loadCalibration(calibration: IStableBalanceCalibration, clear = true) {
    if (calibration && calibration.Settings && calibration.Settings.Forecast && calibration.Settings.Historical) {
      if (clear) {
        this.clearValues();
      }

      this.selectedPolynomialForm = calibration.SelectedPolynomialForm;

      let acquisitionsIds = Array.from(new Set(calibration.SelectedAcquisitionIds)).sort();
      const allAcquisitions = this.getState().acquisitions;
      acquisitionsIds.forEach(acqId => {
        const acquisition = allAcquisitions.find(acq => acq.AcquisitionId === acqId);
        if (acquisition) {
          this.selectedAcquisitions.push(acquisition);
        } else {
          // This selected acquisition doesn't longer exist in the system, so we remove it from the calibration
          acquisitionsIds = acquisitionsIds.filter(id => id !== acqId);
        }
      });

      calibration.SelectedAcquisitionIds = acquisitionsIds;

      const dateRange: IDateRange = {
        StartDate: new Date(calibration.CalibrationStartDate),
        EndDate: new Date(calibration.CalibrationEndDate)
      };
      this.dateRange = dateRange;

      this.fixSettings(calibration);

      this.historicalSettings = calibration.Settings.Historical;
      this.forecastSettings = calibration.Settings.Forecast;
    }
  }

  /**
   * Clears all the values from the observables and global variables in the service
   */
  public clearValues(): void {
    this.dateRange$.next(null);
    this.forecastSettings$.next(null);
    this.historicalSettings$.next(null);
    this.selectedAcquisitions$.next([]);
    this.selectedPolynomialForm$.next(this.currentCalibration ? this.currentCalibration.SelectedPolynomialForm : null);
    this.totalBalances$.next([]);
    this.estimates = [];
    this.canWriteToLocalStorage = false;
  }

  private filterAdjustments(adjustments: IStableBalanceSeries[], startDate: Date): IStableBalanceSeries[] {
    const filteredAdjustments = adjustments.filter(({ PeriodEndDate }) => new Date(PeriodEndDate).getTime() >= startDate.getTime());
    return filteredAdjustments;
  }

  /**
   * * Making sure that **Forecast Method** is in enum format and not string.
   * * Making sure **Historical Damping** is in num format (1 or 0) and not boolean
   */
  public fixSettings(calibration: IStableBalanceCalibration) {
    if (calibration && calibration.Settings) {
      this.fixForecastSettings(calibration.Settings.Forecast);
      this.fixHistoricalSettings(calibration.Settings.Historical);
    }
  }
  /**
   * Making sure that **Forecast Method** is in enum format and not string.
   */
  public fixForecastSettings(forecastSettings: IForecastSettings) {
    if (forecastSettings) {
      if (typeof forecastSettings.ForecastMethod === 'string') {
        // @ts-ignore
        forecastSettings.ForecastMethod = ForecastMethods[forecastSettings.ForecastMethod];
      }
    }
  }
  /**
   * Making sure **Historical Damping** is in num format (1 or 0) and not boolean
   */
  public fixHistoricalSettings(historicalSettings: IHistoricalSettings) {
    if (historicalSettings) {
      historicalSettings.Damping = historicalSettings.Damping ? 1 : 0;
    }
  }

  public runEstimates(
    selectedAcquisitions: number[],
    dateRange: IDateRange,
    adjustments: IStableBalanceSeries[]
  ): Observable<IStableBalanceEstimateResult[]> {
    const selectedProductId = this.menu.selectedProduct.ProductId;

    if (dateRange && isValidDate(dateRange.StartDate) && isValidDate(dateRange.EndDate)) {
      // Pick only adjustments within this date range
      const balanceAdjustments = (adjustments || []).filter(
        a => a.Balance && new Date(a.PeriodEndDate) >= dateRange.StartDate && new Date(a.PeriodEndDate) <= dateRange.EndDate
      );

      // Get Polynomial Forms
      const diff = monthDiff(dateRange.StartDate, dateRange.EndDate);

      // if (diff < 0) {
      //   // Start Date is posterior to Calibration End Date
      //   return of(null);
      // }

      let polynomialOrders = [];
      if (diff >= this.MAX_FUNCTIONAL_FORMS) {
        polynomialOrders = new Array(this.MAX_FUNCTIONAL_FORMS).fill(null).map((e, i) => i + 1);
      } else {
        polynomialOrders = new Array(diff).fill(null).map((e, i) => i + 1);
      }

      const query: IStableBalanceEstimateQuery = {
        StartDate: getDateString(new Date(dateRange.StartDate)),
        EndDate: getDateString(new Date(dateRange.EndDate)),
        ProductId: selectedProductId,
        SelectedAcquisitionIds: selectedAcquisitions.sort(),
        BalanceAdjustments: balanceAdjustments,
        PolynomialOrders: polynomialOrders
      };

      return this.http.post<IStableBalanceEstimateResult[]>(this.ESTIMATE_API, query).pipe(
        map((estimates: IStableBalanceEstimateResult[]) => {
          estimates = estimates.filter(e => e.FittedBalanceSeries);

          estimates.forEach((estimate, i) => {
            estimate.name = estimateNames[i];
            estimate.StartDate = query.StartDate;
            estimate.EndDate = query.EndDate;
            estimate.SelectedAcquisitionIds = query.SelectedAcquisitionIds;
            estimate.ProductId = query.ProductId;
            estimate.PolynomialOrders = [i + 1];
            estimate.FittedBalanceSeries = estimate.FittedBalanceSeries.map(b => ({
              PeriodEndDate: getDateString(b.PeriodEndDate),
              Balance: b.Balance,
              IsHistorical: true
            }));
            estimate.disabled = false;
          });

          this.estimates = estimates;
          return this.estimates;
        })
      );
    }

    return of(null);
  }
}
